import type {
  DiamApplicationContext,
  DiamIdentity,
  DiamResolvedModifierValue,
  DiamTeam,
} from '../../diamApi';
import { Team, TeamLocation, TeamMember } from './types';

function getModifierValue(
  modifiers: Record<string, DiamResolvedModifierValue> | undefined,
  key: string,
): string {
  return modifiers?.[key]?.value?.trim() ?? '';
}

export function mapTeamModifiers(
  modifiers: Record<string, DiamResolvedModifierValue> | undefined,
): string[] {
  return Object.entries(modifiers ?? {})
    .filter(([, resolved]) => Boolean(resolved?.value?.trim()))
    .map(([key, resolved]) => `@${key}:${resolved.value?.trim()}`);
}

export function mapTeamAccounts(
  modifiers: Record<string, DiamResolvedModifierValue> | undefined,
): string[] {
  const account = getModifierValue(modifiers, 'account');
  return account ? [account] : [];
}

export function mapTeamLocations(
  modifiers: Record<string, DiamResolvedModifierValue> | undefined,
): TeamLocation[] {
  const location = getModifierValue(modifiers, 'location');
  return location ? [{ site: location, dataCenters: [] }] : [];
}

export function mapTeamMembers(
  identities: DiamIdentity[] | undefined,
): TeamMember[] {
  return (identities ?? []).map(identity => ({
    id: identity.id,
    name: identity.name ?? '',
    email: identity.email ?? '',
    roles: '',
  }));
}

export function mapApiTeam(
  team: DiamTeam,
  extras?: {
    applicationContexts?: DiamApplicationContext[];
    members?: DiamIdentity[];
  },
): Team {
  return {
    id: team.id,
    name: team.name,
    membersCount: team.identitiesCount ?? extras?.members?.length ?? 0,
    modifiers: mapTeamModifiers(team.modifiers),
    createdAt: team.createdDate ?? '',
    parentTeam: team.parent?.name ?? 'N/A',
    applications: (extras?.applicationContexts ?? []).map(
      application => application.name,
    ),
    accounts: mapTeamAccounts(team.modifiers),
    locations: mapTeamLocations(team.modifiers),
    members: mapTeamMembers(extras?.members),
  };
}
